import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SRAccount } from '@api/v1';
import moment from 'moment-with-locales-es6';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../core/actions';
import { i18n } from '../../../i18n';
import { api } from '../../api';

interface OAuthClient {
	id: number;
	clientId: string;
	name: string;
	scopes: string;
	isActive: boolean;
	lastUsedAt: string | null;
}

interface CreatedOAuthClient extends OAuthClient {
	clientSecret: string;
}

interface OAuthClientFormState {
	name: string;
	scopes: string;
}

const initialFormState: OAuthClientFormState = {
	name: '',
	scopes: ''
};

export function ManageOAuthClientsButton({ account }: { account: SRAccount }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [clients, setClients] = useState<OAuthClient[]>([]);
	const [formState, setFormState] = useState(initialFormState);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createdClient, setCreatedClient] = useState<CreatedOAuthClient | null>(null);
	const [clientPendingRevoke, setClientPendingRevoke] = useState<OAuthClient | null>(null);

	const loadClients = async () => {
		try {
			const data = await api.get<OAuthClient[]>(
				`/api/sysadmin/accounts/${account.id}/oauth/clients`
			);
			setClients(data);
		} catch {
			dispatch(showNotification(i18n.t('sysadmin:oauth_clients_load_error'), 'error'));
		}
	};

	const handleOpen = () => {
		setOpen(true);
		void loadClients();
	};

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		setOpen(false);
		setFormState(initialFormState);
		setCreatedClient(null);
	};

	const handleChange = (field: keyof OAuthClientFormState) => (event) => {
		setFormState((currentState) => ({
			...currentState,
			[field]: event.target.value
		}));
	};

	const handleCreate = async () => {
		setIsSubmitting(true);

		try {
			const created = await api.post<OAuthClientFormState, CreatedOAuthClient>(
				`/api/sysadmin/accounts/${account.id}/oauth/clients`,
				formState
			);
			setCreatedClient(created);
			setFormState(initialFormState);
			dispatch(showNotification(i18n.t('sysadmin:oauth_client_create_success'), 'success'));
			await loadClients();
		} catch {
			dispatch(showNotification(i18n.t('sysadmin:oauth_client_create_error'), 'error'));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRevoke = async (clientId: string) => {
		try {
			await api.delete(`/api/sysadmin/accounts/${account.id}/oauth/clients/${clientId}`);
			dispatch(showNotification(i18n.t('sysadmin:oauth_client_revoke_success'), 'success'));
			await loadClients();
		} catch {
			dispatch(showNotification(i18n.t('sysadmin:oauth_client_revoke_error'), 'error'));
		} finally {
			setClientPendingRevoke(null);
		}
	};

	return (
		<>
			<Button variant="outlined" color="primary" onClick={handleOpen}>
				{t('sysadmin:oauth_clients_manage')}
			</Button>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
				<DialogTitle>{t('sysadmin:oauth_clients_title')}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>{t('sysadmin:oauth_clients_hint')}</DialogContentText>

					{createdClient ? (
						<Alert severity="warning" sx={{ mb: 2 }}>
							<Typography variant="body2" sx={{ mb: 1 }}>
								{t('sysadmin:oauth_client_secret_hint')}
							</Typography>
							<TextField
								fullWidth
								label={t('sysadmin:oauth_client_secret_label')}
								value={createdClient.clientSecret}
								InputProps={{ readOnly: true }}
							/>
						</Alert>
					) : null}

					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>{t('sysadmin:oauth_client_name')}</TableCell>
								<TableCell>{t('sysadmin:oauth_client_id')}</TableCell>
								<TableCell>{t('sysadmin:oauth_client_scopes')}</TableCell>
								<TableCell>{t('sysadmin:oauth_client_status')}</TableCell>
								<TableCell>{t('sysadmin:oauth_client_last_used')}</TableCell>
								<TableCell align="right">{t('sysadmin:oauth_client_revoke')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{clients.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6}>
										<Typography variant="body2" color="text.secondary">
											{t('sysadmin:oauth_client_none')}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								clients.map((client) => (
									<TableRow key={client.id}>
										<TableCell>{client.name}</TableCell>
										<TableCell>{client.clientId}</TableCell>
										<TableCell>{client.scopes}</TableCell>
										<TableCell>
											<Chip
												size="small"
												color={client.isActive ? 'success' : 'default'}
												label={
													client.isActive
														? t('sysadmin:oauth_client_active')
														: t('sysadmin:oauth_client_inactive')
												}
											/>
										</TableCell>
										<TableCell>
											{client.lastUsedAt ? moment(client.lastUsedAt).format('L LT') : '-'}
										</TableCell>
										<TableCell align="right">
											<Tooltip title={t('sysadmin:oauth_client_revoke')}>
												<span>
													<IconButton
														size="small"
														color="error"
														disabled={!client.isActive}
														onClick={() => setClientPendingRevoke(client)}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>

					<Box sx={{ mt: 3 }}>
						<Typography variant="subtitle1" sx={{ mb: 1 }}>
							{t('sysadmin:oauth_client_add')}
						</Typography>
						<TextField
							fullWidth
							margin="normal"
							label={t('sysadmin:oauth_client_name')}
							value={formState.name}
							onChange={handleChange('name')}
						/>
						<TextField
							fullWidth
							margin="normal"
							label={t('sysadmin:oauth_client_scopes')}
							value={formState.scopes}
							onChange={handleChange('scopes')}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary" disabled={isSubmitting}>
						{t('sysadmin:restrictions_dialog_cancel')}
					</Button>
					<Button
						onClick={handleCreate}
						color="primary"
						disabled={!formState.name.trim() || isSubmitting}
					>
						{t('sysadmin:oauth_client_add')}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={!!clientPendingRevoke} onClose={() => setClientPendingRevoke(null)}>
				<DialogTitle>{t('sysadmin:oauth_client_revoke_confirm_title')}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t('sysadmin:oauth_client_revoke_confirm_text', { name: clientPendingRevoke?.name })}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setClientPendingRevoke(null)} color="primary">
						{t('sysadmin:restrictions_dialog_cancel')}
					</Button>
					<Button
						onClick={() => clientPendingRevoke && handleRevoke(clientPendingRevoke.clientId)}
						color="error"
					>
						{t('sysadmin:oauth_client_revoke')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
